import { billingService } from '@common/api/billingService';
import { useAuth } from '@/contexts/AuthContext';
import {
    applyPaymentReturnPlanToProfile,
    paymentReturnPlanCodesMatch,
} from '@/contexts/AuthContext/paymentReturnPlanSync';
import { refreshProfileFromServer } from '@/contexts/AuthContext/refreshProfileFromServer';
import { logSentryEvent, captureSentryError } from '@/lib/sentry';
import { runtimeAccessConfig } from '@/config/runtimeAccess';
import WaitingPage from '@/components/WaitingPage';
import { type Translate, useTranslation } from '@client-app/i18n';
import {
    clearPendingCreditTopupCheckoutSessionId,
    readPendingCreditTopupCheckoutSessionId,
} from '@/components/Settings/components/creditTopupReturnParams';
import {
    buildFinalPaymentRoute,
    readPaymentReturnState,
    shouldVerifySubscriptionReturnBeforeFinalRoute,
    type PaymentReturnState,
} from './paymentReturnFlow';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type PaymentReturnViewState =
    | 'checking-payment'
    | 'syncing-payment'
    | 'refreshing-workspace'
    | 'ready'
    | 'error';

const wait = (ms: number) => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
});

const PAYMENT_RETURN_INTRO_DELAY_MS = 240;
const PAYMENT_RETURN_READY_DELAY_MS = 160;
const PAYMENT_RETURN_SYNC_TIMEOUT_MS = 5_000;
const PAYMENT_RETURN_REFRESH_TIMEOUT_MS = 3_000;
const PAYMENT_RETURN_PAGE_MAX_WAIT_MS = 7_000;
const PAYMENT_RETURN_SUBSCRIPTION_SYNC_ATTEMPTS = 8;
const PAYMENT_RETURN_SUBSCRIPTION_SYNC_DELAY_MS = 1_250;

const withPaymentReturnTimeout = async <T,>(
    promise: Promise<T>,
    timeoutMs: number,
    label: string,
): Promise<T> => {
    let timeoutId: number | null = null;
    try {
        return await Promise.race([
            promise,
            new Promise<T>((_resolve, reject) => {
                timeoutId = window.setTimeout(() => {
                    reject(new Error(`${label} timed out after ${timeoutMs}ms`));
                }, timeoutMs);
            }),
        ]);
    } finally {
        if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
        }
    }
};

const runOptionalPaymentReturnStep = async <T,>(params: {
    state: PaymentReturnState;
    viewState: PaymentReturnViewState;
    promise: Promise<T>;
    timeoutMs: number;
    transitionReason: string;
}): Promise<T | null> => {
    try {
        return await withPaymentReturnTimeout(
            params.promise,
            params.timeoutMs,
            params.transitionReason,
        );
    } catch (error) {
        console.warn('[PaymentReturn] Optional payment return step skipped', {
            ...buildPaymentReturnLogContext(params.state, params.viewState),
            transition_reason: params.transitionReason,
            error,
        });
        logSentryEvent('payment_return.optional_step_skipped', {
            area: 'payment',
            transition_reason: params.transitionReason,
            ...buildPaymentReturnLogContext(params.state, params.viewState),
        }, 'warn');
        return null;
    }
};

const runCreditTopupSyncPaymentReturnStep = async (params: {
    state: PaymentReturnState;
    promise: ReturnType<typeof billingService.syncCreditTopupCheckoutSession>;
}): Promise<boolean> => {
    try {
        await withPaymentReturnTimeout(
            params.promise,
            PAYMENT_RETURN_SYNC_TIMEOUT_MS,
            'credit_topup_sync',
        );
        return true;
    } catch (error) {
        console.warn('[PaymentReturn] Optional payment return step skipped', {
            ...buildPaymentReturnLogContext(params.state, 'syncing-payment'),
            transition_reason: 'credit_topup_sync',
            error,
        });
        logSentryEvent('payment_return.optional_step_skipped', {
            area: 'payment',
            transition_reason: 'credit_topup_sync',
            ...buildPaymentReturnLogContext(params.state, 'syncing-payment'),
        }, 'warn');
        return false;
    }
};

const getStageLabel = (viewState: PaymentReturnViewState, t: Translate): string => {
    switch (viewState) {
    case 'checking-payment':
    case 'syncing-payment':
    case 'refreshing-workspace':
    case 'ready':
        return t('website.waiting.paymentReturn.message');
    case 'error':
        return t('website.waiting.paymentReturn.errorMessage');
    default:
        return t('website.waiting.paymentReturn.message');
    }
};

const buildPaymentReturnLogContext = (
    state: PaymentReturnState,
    viewState: PaymentReturnViewState,
) => ({
    route_kind: 'payment_return',
    current_state: viewState,
    payment_return_kind: state.kind,
    payment_return_result: state.result,
    payment_return_mode: state.mode,
    workspace_id_present: Boolean(state.workspaceId),
    session_id_present: Boolean(state.sessionId),
});

const isFutureDate = (value?: string | null): boolean => {
    if (!value) return false;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) && timestamp > Date.now();
};

const profileReflectsPaidSubscription = (
    verifiedProfile: Awaited<ReturnType<typeof refreshProfileFromServer>> | null,
    expectedPlanCode?: string | null,
    expectedBillingCycle?: string | null,
): boolean => {
    const workspacePlan = verifiedProfile?.workspacePlan;
    if (!workspacePlan) {
        return false;
    }

    const status = workspacePlan.status?.trim().toLowerCase();
    const badge = workspacePlan.badge?.trim().toLowerCase();
    const actionLabel = workspacePlan.actionLabel?.trim().toLowerCase();
    const expectedCycle = expectedBillingCycle === 'monthly' || expectedBillingCycle === 'yearly'
        ? expectedBillingCycle
        : null;
    const actualCycle = workspacePlan.billingCycle === 'monthly' || workspacePlan.billingCycle === 'yearly'
        ? workspacePlan.billingCycle
        : null;
    const isTrial = status === 'trial'
        || status === 'trialing'
        || badge === 'trial'
        || actionLabel === 'start subscription'
        || isFutureDate(workspacePlan.trialEndAt);

    if (!paymentReturnPlanCodesMatch(expectedPlanCode, workspacePlan.planCode)) {
        return false;
    }

    if (expectedCycle && actualCycle !== expectedCycle) {
        return false;
    }

    return (status === 'active' || status === 'paid') && !isTrial;
};

const PaymentReturnPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const {
        currentWorkspace,
        hideGlobalLoading,
        profile,
        applyWorkspaceSubscriptionPlan,
        refreshWorkspaces,
        setCurrentWorkspace,
        setProfile,
        userBilling,
    } = useAuth();
    const paymentReturnState = useMemo(
        () => readPaymentReturnState(
            searchParams,
            readPendingCreditTopupCheckoutSessionId(),
        ),
        [searchParams],
    );
    const paymentReturnKey = useMemo(() => searchParams.toString(), [searchParams]);
    const [viewState, setViewState] = useState<PaymentReturnViewState>('checking-payment');
    const [errorRoute, setErrorRoute] = useState<string | null>(null);
    const hasNavigatedRef = useRef(false);
    const viewStateRef = useRef<PaymentReturnViewState>('checking-payment');
    const creditTopupCheckedRef = useRef(false);
    const paymentReturnStateRef = useRef(paymentReturnState);
    const currentWorkspaceIdRef = useRef(currentWorkspace?.id || null);
    const profileRef = useRef(profile);
    const userBillingRef = useRef(userBilling);
    const paymentReturnActionsRef = useRef<{
        applyWorkspaceSubscriptionPlan: typeof applyWorkspaceSubscriptionPlan;
        navigateToFinalRoute: (
            state: PaymentReturnState,
            creditTopupChecked?: boolean,
            transitionReason?: string,
        ) => void;
        refreshWorkspaces: typeof refreshWorkspaces;
        setCurrentWorkspace: typeof setCurrentWorkspace;
        setProfile: typeof setProfile;
    }>({
        applyWorkspaceSubscriptionPlan,
        navigateToFinalRoute: (
            _state: PaymentReturnState,
            _creditTopupChecked?: boolean,
            _transitionReason?: string,
        ) => {
            // Replaced after the navigation callback is initialized.
        },
        refreshWorkspaces,
        setCurrentWorkspace,
        setProfile,
    });

    const navigateToFinalRoute = useCallback((
        state: PaymentReturnState,
        creditTopupChecked: boolean = false,
        transitionReason: string = 'flow_completed',
    ) => {
        if (hasNavigatedRef.current) {
            return;
        }

        hasNavigatedRef.current = true;
        const finalRoute = buildFinalPaymentRoute({
            state,
            creditTopupChecked,
            disableCloudRuntime: runtimeAccessConfig.disableCloudRuntime,
        });
        console.info('[PaymentReturn] Navigating to final payment route', {
            ...buildPaymentReturnLogContext(state, 'ready'),
            transition_reason: transitionReason,
            final_route: finalRoute,
        });
        navigate(finalRoute, { replace: true });
    }, [navigate]);

    useEffect(() => {
        hideGlobalLoading();
        return () => hideGlobalLoading();
    }, [hideGlobalLoading]);

    useEffect(() => {
        viewStateRef.current = viewState;
    }, [viewState]);

    useEffect(() => {
        paymentReturnStateRef.current = paymentReturnState;
    }, [paymentReturnState]);

    useEffect(() => {
        currentWorkspaceIdRef.current = currentWorkspace?.id || null;
    }, [currentWorkspace?.id]);

    useEffect(() => {
        profileRef.current = profile;
    }, [profile]);

    useEffect(() => {
        userBillingRef.current = userBilling;
    }, [userBilling]);

    useEffect(() => {
        paymentReturnActionsRef.current = {
            applyWorkspaceSubscriptionPlan,
            navigateToFinalRoute,
            refreshWorkspaces,
            setCurrentWorkspace,
            setProfile,
        };
    }, [
        applyWorkspaceSubscriptionPlan,
        navigateToFinalRoute,
        refreshWorkspaces,
        setCurrentWorkspace,
        setProfile,
    ]);

    useEffect(() => {
        const stateForRun = paymentReturnStateRef.current;
        hasNavigatedRef.current = false;
        creditTopupCheckedRef.current = false;

        let cancelled = false;

        const finalizeCreditTopupSuccess = async () => {
            setViewState('syncing-payment');
            let creditTopupChecked = false;
            if (stateForRun.sessionId) {
                creditTopupChecked = await runCreditTopupSyncPaymentReturnStep({
                    state: stateForRun,
                    promise: billingService.syncCreditTopupCheckoutSession({
                        sessionId: stateForRun.sessionId,
                    }),
                });
                creditTopupCheckedRef.current = creditTopupChecked;
            } else {
                console.warn('[PaymentReturn] Credit top-up return missing checkout session id', {
                    ...buildPaymentReturnLogContext(stateForRun, 'syncing-payment'),
                    transition_reason: 'missing_session_id',
                });
            }

            if (cancelled) {
                return;
            }

            setViewState('refreshing-workspace');
            await runOptionalPaymentReturnStep({
                state: stateForRun,
                viewState: 'refreshing-workspace',
                promise: Promise.allSettled([
                    userBillingRef.current.refreshAll(),
                    paymentReturnActionsRef.current.refreshWorkspaces({
                        silent: true,
                        skipEmptyRedirect: true,
                        preferredWorkspaceId: stateForRun.workspaceId || undefined,
                    }),
                ]),
                timeoutMs: PAYMENT_RETURN_REFRESH_TIMEOUT_MS,
                transitionReason: 'credit_topup_refresh',
            });

            clearPendingCreditTopupCheckoutSessionId();
            if (!cancelled) {
                setViewState('ready');
                await wait(PAYMENT_RETURN_READY_DELAY_MS);
                paymentReturnActionsRef.current.navigateToFinalRoute(stateForRun, creditTopupChecked);
            }
        };

        const finalizeSubscriptionSuccess = async () => {
            setViewState('syncing-payment');
            const requestedWorkspaceId = stateForRun.workspaceId || currentWorkspaceIdRef.current || null;

            if (stateForRun.workspaceId) {
                paymentReturnActionsRef.current.setCurrentWorkspace(stateForRun.workspaceId);
            }
            if (requestedWorkspaceId) {
                paymentReturnActionsRef.current.applyWorkspaceSubscriptionPlan(
                    requestedWorkspaceId,
                    stateForRun.planCode,
                    stateForRun.planName,
                    stateForRun.billingCycle,
                );
            }

            const optimisticProfile = applyPaymentReturnPlanToProfile(
                profileRef.current,
                stateForRun.planCode,
                stateForRun.planName,
                stateForRun.billingCycle,
            );
            if (optimisticProfile) {
                paymentReturnActionsRef.current.setProfile(optimisticProfile);
                userBillingRef.current.applyWorkspacePlan(optimisticProfile.workspacePlan);
            }

            if (!shouldVerifySubscriptionReturnBeforeFinalRoute(stateForRun)) {
                setViewState('refreshing-workspace');
                const warmupResults = await runOptionalPaymentReturnStep({
                    state: stateForRun,
                    viewState: 'refreshing-workspace',
                    promise: Promise.allSettled([
                        paymentReturnActionsRef.current.refreshWorkspaces({
                            silent: true,
                            skipEmptyRedirect: true,
                            preferredWorkspaceId: stateForRun.workspaceId || undefined,
                        }),
                        refreshProfileFromServer({
                            workspaceId: requestedWorkspaceId,
                            includeSetupCardStatus: Boolean(requestedWorkspaceId),
                        }),
                        userBillingRef.current.refreshAll(),
                    ]),
                    timeoutMs: PAYMENT_RETURN_REFRESH_TIMEOUT_MS,
                    transitionReason: 'onboarding_subscription_return_warmup',
                });

                if (cancelled) {
                    return;
                }

                const warmupProfileResult = Array.isArray(warmupResults)
                    ? warmupResults[1]
                    : null;
                const warmupProfile = warmupProfileResult?.status === 'fulfilled'
                    ? applyPaymentReturnPlanToProfile(
                        warmupProfileResult.value,
                        stateForRun.planCode,
                        stateForRun.planName,
                        stateForRun.billingCycle,
                    )
                    : null;

                if (warmupProfile) {
                    paymentReturnActionsRef.current.setProfile(warmupProfile);
                    userBillingRef.current.applyWorkspacePlan(warmupProfile.workspacePlan);
                }

                if (requestedWorkspaceId && stateForRun.planCode) {
                    paymentReturnActionsRef.current.applyWorkspaceSubscriptionPlan(
                        requestedWorkspaceId,
                        stateForRun.planCode,
                        stateForRun.planName,
                        stateForRun.billingCycle,
                    );
                }

                setViewState('ready');
                await wait(PAYMENT_RETURN_READY_DELAY_MS);
                if (cancelled) {
                    return;
                }
                paymentReturnActionsRef.current.navigateToFinalRoute(
                    stateForRun,
                    false,
                    'onboarding_workspace_return_ready',
                );
                return;
            }

            let subscriptionReady = false;

            for (let attempt = 1; attempt <= PAYMENT_RETURN_SUBSCRIPTION_SYNC_ATTEMPTS; attempt += 1) {
                if (cancelled) {
                    return;
                }

                setViewState(attempt === 1 ? 'syncing-payment' : 'refreshing-workspace');
                const subscriptionResults = await runOptionalPaymentReturnStep({
                    state: stateForRun,
                    viewState: attempt === 1 ? 'syncing-payment' : 'refreshing-workspace',
                    promise: Promise.allSettled([
                        paymentReturnActionsRef.current.refreshWorkspaces({
                            silent: true,
                            skipEmptyRedirect: true,
                            preferredWorkspaceId: stateForRun.workspaceId || undefined,
                        }),
                        refreshProfileFromServer({
                            workspaceId: requestedWorkspaceId,
                            includeSetupCardStatus: Boolean(requestedWorkspaceId),
                        }),
                        userBillingRef.current.refreshAll(),
                    ]),
                    timeoutMs: PAYMENT_RETURN_SYNC_TIMEOUT_MS,
                    transitionReason: `subscription_refresh_attempt_${attempt}`,
                });

                if (cancelled) {
                    return;
                }

                const profileResult = Array.isArray(subscriptionResults)
                    ? subscriptionResults[1]
                    : null;
                const verifiedServerProfile = profileResult?.status === 'fulfilled'
                    ? profileResult.value
                    : null;

                if (verifiedServerProfile) {
                    const displayProfile = applyPaymentReturnPlanToProfile(
                        verifiedServerProfile,
                        stateForRun.planCode,
                        stateForRun.planName,
                        stateForRun.billingCycle,
                    );
                    paymentReturnActionsRef.current.setProfile(displayProfile);
                    userBillingRef.current.applyWorkspacePlan(displayProfile?.workspacePlan);
                    subscriptionReady = profileReflectsPaidSubscription(
                        verifiedServerProfile,
                        stateForRun.planCode,
                        stateForRun.billingCycle,
                    );
                }

                if (requestedWorkspaceId && stateForRun.planCode) {
                    paymentReturnActionsRef.current.applyWorkspaceSubscriptionPlan(
                        requestedWorkspaceId,
                        stateForRun.planCode,
                        stateForRun.planName,
                        stateForRun.billingCycle,
                    );
                }

                if (subscriptionReady) {
                    break;
                }

                if (attempt < PAYMENT_RETURN_SUBSCRIPTION_SYNC_ATTEMPTS) {
                    await wait(PAYMENT_RETURN_SUBSCRIPTION_SYNC_DELAY_MS);
                }
            }

            if (!subscriptionReady) {
                throw new Error('Subscription payment is still syncing. Please wait before continuing.');
            }

            if (!cancelled) {
                setViewState('ready');
                await wait(PAYMENT_RETURN_READY_DELAY_MS);
                paymentReturnActionsRef.current.navigateToFinalRoute(
                    stateForRun,
                    false,
                    'subscription_verified_ready',
                );
            }
        };

        const runPaymentReturnFlow = async () => {
            setErrorRoute(null);
            setViewState('checking-payment');
            console.info('[PaymentReturn] Return detected', {
                ...buildPaymentReturnLogContext(stateForRun, 'checking-payment'),
                transition_reason: 'return_detected',
            });
            logSentryEvent('payment_return.detected', {
                area: 'payment',
                kind: stateForRun.kind,
                result: stateForRun.result,
                mode: stateForRun.mode,
                workspace_id_present: Boolean(stateForRun.workspaceId),
                session_id_present: Boolean(stateForRun.sessionId),
            });

            if (!stateForRun.kind || !stateForRun.result) {
                throw new Error('Missing payment return kind or result');
            }

            await wait(PAYMENT_RETURN_INTRO_DELAY_MS);
            if (cancelled) {
                return;
            }

            if (stateForRun.result === 'cancel') {
                setViewState('ready');
                await wait(PAYMENT_RETURN_READY_DELAY_MS);
                if (!cancelled) {
                    paymentReturnActionsRef.current.navigateToFinalRoute(stateForRun);
                }
                return;
            }

            if (stateForRun.kind === 'credit_topup') {
                await finalizeCreditTopupSuccess();
                return;
            }

            await finalizeSubscriptionSuccess();
        };

        void runPaymentReturnFlow().catch((error) => {
            const finalRoute = buildFinalPaymentRoute({
                state: stateForRun,
                disableCloudRuntime: runtimeAccessConfig.disableCloudRuntime,
            });
            setErrorRoute(finalRoute);
            setViewState('error');
            console.error('[PaymentReturn] Failed to finish payment return flow', error);
            logSentryEvent('payment_return.failed', {
                area: 'payment',
                ...buildPaymentReturnLogContext(stateForRun, 'error'),
            }, 'error');
            captureSentryError(error, {
                area: 'payment',
                flow: 'payment_return',
                payment_return_kind: stateForRun.kind,
                payment_return_result: stateForRun.result,
            });
        });

        return () => {
            cancelled = true;
        };
    }, [paymentReturnKey]);

    useEffect(() => {
        if (paymentReturnState.kind === 'subscription' && paymentReturnState.result === 'success') {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            if (hasNavigatedRef.current) {
                return;
            }

            const currentViewState = viewStateRef.current;
            const currentPaymentReturnState = paymentReturnStateRef.current;
            const checkedCreditTopupBeforeTimeout = currentPaymentReturnState.kind === 'credit_topup'
                ? creditTopupCheckedRef.current
                : false;
            console.warn('[PaymentReturn] Page-level timeout reached, continuing to final route', {
                ...buildPaymentReturnLogContext(currentPaymentReturnState, currentViewState),
                transition_reason: 'page_timeout',
                credit_topup_checked: checkedCreditTopupBeforeTimeout,
            });
            logSentryEvent('payment_return.page_timeout', {
                area: 'payment',
                ...buildPaymentReturnLogContext(currentPaymentReturnState, currentViewState),
                credit_topup_checked: checkedCreditTopupBeforeTimeout,
            }, 'warn');
            navigateToFinalRoute(currentPaymentReturnState, checkedCreditTopupBeforeTimeout, 'page_timeout');
        }, PAYMENT_RETURN_PAGE_MAX_WAIT_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [navigateToFinalRoute, paymentReturnKey, paymentReturnState.kind, paymentReturnState.result]);

    const continueAfterError = () => {
        if (errorRoute) {
            hasNavigatedRef.current = true;
            navigate(errorRoute, { replace: true });
            return;
        }

        navigateToFinalRoute(paymentReturnState, false, 'manual_continue');
    };

    return (
        <WaitingPage
            title={t('website.waiting.paymentReturn.title')}
            message={getStageLabel(viewState, t)}
            busy={viewState !== 'error'}
            actionLabel={viewState === 'error' ? t('website.waiting.paymentReturn.continueAction') : undefined}
            onAction={viewState === 'error' ? continueAfterError : undefined}
        />
    );
};

export default PaymentReturnPage;
